"use client";

import * as React from "react";
import { NAV, SITE } from "../../data/site";
import { useScrollspy } from "../../hooks";

const IDS = NAV.map((n) => n.id);

export default function Header({
  dark,
  onToggleTheme,
}: {
  dark: boolean;
  onToggleTheme: () => void;
}) {
  const active = useScrollspy(IDS);
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  React.useEffect(() => {
    let frame = 0;
    const measure = () => {
      frame = 0;
      const max = document.body.scrollHeight - window.innerHeight;
      setScrolled(window.scrollY > 12);
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  React.useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <a
        href="#work"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-surface focus:px-3 focus:py-2 focus:text-sm"
      >
        Skip to content
      </a>

      <header
        className={[
          "fixed inset-x-0 top-0 z-50 transition-colors duration-300",
          scrolled
            ? "border-b bg-bg/80 backdrop-blur-md supports-[backdrop-filter]:bg-bg/65"
            : "border-b border-transparent",
        ].join(" ")}
        style={{ height: "var(--header-h)" }}
      >
        <div className="shell flex h-full items-center justify-between gap-6">
          <a
            href="#top"
            className="group flex items-baseline gap-2.5 no-underline"
            aria-label="Back to top"
          >
            <span className="text-sm font-semibold tracking-tight text-ink">
              {SITE.name}
            </span>
            <span className="hidden text-2xs text-faint sm:inline">
              {SITE.role}
            </span>
          </a>

          <nav className="hidden items-center gap-1 md:flex">
            {NAV.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={[
                  "rounded-md px-2.5 py-1.5 text-sm no-underline transition-colors",
                  active === item.id ? "text-ink" : "text-muted hover:text-ink",
                ].join(" ")}
                aria-current={active === item.id ? "true" : undefined}
              >
                {item.label}
                <span
                  className={[
                    "mx-auto mt-0.5 block h-px origin-center bg-accent transition-transform duration-300",
                    active === item.id ? "scale-x-100" : "scale-x-0",
                  ].join(" ")}
                />
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-1.5">
            <a
              href={SITE.links.resume}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm hidden sm:inline-flex"
            >
              Resume
            </a>
            <ThemeToggle dark={dark} onToggle={onToggleTheme} />
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="btn btn-sm w-9 !px-0 md:hidden"
              aria-label={open ? "Close menu" : "Open menu"}
              aria-expanded={open}
            >
              <Burger open={open} />
            </button>
          </div>
        </div>

        {/* read-progress hairline */}
        <div
          className="absolute inset-x-0 bottom-0 h-px origin-left bg-accent transition-transform duration-150 ease-out"
          style={{
            transform: `scaleX(${progress})`,
            opacity: scrolled ? 1 : 0,
          }}
        />
      </header>

      {/* mobile sheet */}
      <div
        className={[
          "fixed inset-0 z-40 bg-bg/95 backdrop-blur-md transition-opacity duration-200 md:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        ].join(" ")}
        style={{ paddingTop: "var(--header-h)" }}
      >
        <nav className="shell flex flex-col py-6">
          {NAV.map((item, i) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={() => setOpen(false)}
              className="flex items-baseline gap-4 border-b py-4 text-2xl tracking-tight no-underline"
            >
              <span className="eyebrow">{String(i + 1).padStart(2, "0")}</span>
              {item.label}
            </a>
          ))}
          <a
            href={SITE.links.resume}
            target="_blank"
            rel="noopener noreferrer"
            className="btn mt-6"
            onClick={() => setOpen(false)}
          >
            Download resume
          </a>
        </nav>
      </div>
    </>
  );
}

function Burger({ open }: { open: boolean }) {
  return (
    <span className="relative block h-3.5 w-4" aria-hidden>
      <span
        className="absolute left-0 top-0 h-px w-full bg-current transition-transform duration-200"
        style={{
          transform: open ? "translateY(6px) rotate(45deg)" : undefined,
        }}
      />
      <span
        className="absolute left-0 top-1.5 h-px w-full bg-current transition-opacity duration-200"
        style={{ opacity: open ? 0 : 1 }}
      />
      <span
        className="absolute left-0 top-3 h-px w-full bg-current transition-transform duration-200"
        style={{
          transform: open ? "translateY(-6px) rotate(-45deg)" : undefined,
        }}
      />
    </span>
  );
}

function ThemeToggle({
  dark,
  onToggle,
}: {
  dark: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="btn btn-sm w-9 !px-0"
      aria-label={`Switch to ${dark ? "light" : "dark"} theme`}
      title={`Switch to ${dark ? "light" : "dark"} theme`}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden>
        {dark ? (
          <g
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
          >
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.2 5.2l1.4 1.4M17.4 17.4l1.4 1.4M18.8 5.2l-1.4 1.4M6.6 17.4l-1.4 1.4" />
          </g>
        ) : (
          <path
            fill="currentColor"
            d="M20.5 14.2A8.5 8.5 0 0 1 9.8 3.5a8.5 8.5 0 1 0 10.7 10.7Z"
          />
        )}
      </svg>
    </button>
  );
}
